import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import type { AuthCodeResponse, Tenant } from '@mtm/auth';
import type { User } from '../interfaces';
import { AuthRedirectService } from '../../login/auth-redirect/auth-redirect.service';
import { AuthService } from './auth.service';
import { FeatureConfigService } from './feature-config.service';
import { NotificationService } from './notification.service';
import { TranslateService } from './translate.service';
import { EmitterService } from './emitter.service';
import { UserLoggedOutEvent } from '../models/events';
import { AUTH_LANGUAGE_STORAGE_KEY, REDIRECT_URI_STORAGE_KEY } from '../constants';
import { environment } from '../../../environments/environment';
import { rememberedShareLink } from '../auth/last-share-link';

/**
 * Glue between the IAM auth SDK (`@mtm/auth`) and the legacy localStorage-based
 * session this app relies on. The SDK drives the login UI and mints an "app
 * token"; this service stores that token as `localStorage['app_token']` — the
 * SAME key the SDK itself writes (shared slot, aligned with core-fe) — and
 * hydrates the legacy `User`/permissions from the gateway so the existing
 * interceptor, guards, and 50+ downstream consumers keep working unchanged.
 */
@Injectable({ providedIn: 'root' })
export class MtmAuthGlueService {
  private readonly bridge = inject(MtmAuthBridgeService);
  private readonly router = inject(Router);
  private readonly authRedirectService = inject(AuthRedirectService);
  private readonly authService = inject(AuthService);
  private readonly featureConfigService = inject(FeatureConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly translate: TranslateService = inject(TranslateService);

  // On the SSO callback page the IAM SDK can drive the login TWICE: once via the app-initializer
  // session auto-restore (`initialize$` -> `_finishSessionRestore` -> app token) and once via the
  // explicit `?token=` callback (`MtmAuthRedirectComponent` -> onLoginSuccess). Both navigate,
  // producing a callback -> workspace -> callback -> workspace loop. This guard ensures the
  // post-login navigation runs once per login; it is reset on logout for the next login.
  private postAuthHandled = false;

  constructor() {
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.postAuthHandled = false;
      });
  }

  /** Credentials verified by IAM. Resolve the tenant, then head to the exchange. */
  public onLoginSuccess(): void {
    // The session auto-restore already drove the post-login navigation — skip this redundant pass.
    if (this.postAuthHandled) {
      return;
    }
    this.bridge.resolvePostLoginTenant$(environment.iam.serviceUuid).subscribe((result) => {
      // `in` narrowing rather than the `autoSelected` discriminant: this project
      // has strictNullChecks off, so boolean-literal discriminants don't narrow.
      if ('authCode' in result) {
        this.router.navigate([environment.iam.authProcessUrl], {
          queryParams: { ...result.authCode, redirect_uri: this.popRedirectUri() },
        });
      } else {
        this.goToTenantSelect(result.tenants);
      }
    });
  }

  /** Cross-site case: multiple tenants require an explicit selection. */
  public onTenantRequired(tenants: Tenant[]): void {
    this.goToTenantSelect(tenants);
  }

  /**
   * The auth-code exchange produced an app token. Persist it as the legacy token
   * and hydrate the legacy session (user + profile settings + permissions) from
   * the gateway, then navigate to the return URL or the initial route.
   */
  public onAppTokenReady(appToken: string): void {
    // Admin "switch user" stores a gateway switch-user token in `localStorage['app_token']` (and
    // the target user under the shared `auth_user` key) that the IAM SDK knows nothing about. On
    // the post-switch reload the SDK validates the admin's still-valid IAM session cookie and
    // restores it: it overwrites `auth_user` with the admin IAM identity (which has no legacy
    // `username`, so `getAuthUserName()` → null → `GET /api/users/null/...`) and mints a fresh
    // ADMIN app token, firing this callback. Letting that flow through would snap the session back
    // to the admin. KNOWN LIMITATION: since `app_token` is now a single slot shared with the SDK,
    // the SDK's restore already overwrote the switch-user token before this callback runs, so
    // `getAuthToken()` below returns the admin's fresh app token — impersonation across a reload
    // is degraded pending a dedicated switch-user rework.
    if (this.authService.isImpersonating()) {
      const switchToken = this.authService.getAuthToken();
      const onBehalfRaw = localStorage.getItem('onbehalf_user');
      const onBehalfUser = onBehalfRaw ? (JSON.parse(onBehalfRaw) as User) : null;
      if (switchToken && onBehalfUser?.username) {
        // Restore the impersonated user SYNCHRONOUSLY so the app initializer, router guards and
        // shell read the right identity immediately (the app bootstraps before the async
        // re-hydration below resolves). Then refresh permissions / feature config / settings for
        // the target from the still-valid switch-user token (NOT the admin `appToken`).
        localStorage.setItem('auth_user', onBehalfRaw);
        this.authRedirectService.loginWithToken(switchToken, onBehalfUser.username).subscribe({
          next: () => this.redirectAfterAuthentication(),
          error: () => this.router.navigate(['/entry/login']),
        });
      } else {
        this.redirectAfterAuthentication();
      }
      return;
    }
    const iamUser = this.bridge.currentUser();
    const email = iamUser?.email;
    if (!email) {
      this.router.navigate(['/entry/login']);
      return;
    }
    // Dedupe the two concurrent SSO flows (auto-restore vs explicit callback) — run the
    // hydrate + navigate once per login.
    if (this.postAuthHandled) {
      return;
    }
    this.postAuthHandled = true;
    // Hydrate the legacy session from the gateway user endpoint (`GET /api/users/{email}`), then
    // backfill identity the gateway may omit (companyId, username, email, names) from the IAM
    // identity so `auth_user` reliably carries it — the left nav, the conversation SDK `?email=`
    // query, and `/api/users/{username}/...` calls all read these. companyId maps from the tenant
    // UUID: read the SDK's in-memory value first (set synchronously on tenant resolution and kept
    // for token renewals); `localStorage['auth_tenant_uuid']` is sometimes not yet persisted, and
    // without companyId `setAuthUser()` skips its write, leaving the SDK's IAM user in `auth_user`.
    const tenantUuid = this.bridge.getTenantUuid() ?? localStorage.getItem('auth_tenant_uuid') ?? undefined;
    const fullName = [iamUser?.firstName, iamUser?.lastName].filter(Boolean).join(' ').trim() || undefined;
    const identityOverrides: Partial<User> = {
      username: email,
      email,
      companyId: tenantUuid,
      firstName: iamUser?.firstName,
      lastName: iamUser?.lastName,
      fullName,
    };
    this.authRedirectService.loginWithToken(appToken, email, undefined, identityOverrides).subscribe({
      next: () => this.redirectAfterAuthentication(),
      error: () => {
        // Allow a retry: the post-login flow did not complete.
        this.postAuthHandled = false;
        this.router.navigate(['/entry/login']);
      },
    });
  }

  private goToTenantSelect(tenants: Tenant[]): void {
    // No tenant means nothing to pick: the picker would be a dead end. That is a guest — an account
    // that only reaches what was shared with it — or an account removed from every tenant.
    if (tenants.length === 0) {
      // A guest's app token cannot be renewed (the SDK renews for a tenant), so an expired guest
      // session lands here too. Send them back to the share link they last opened: a guest auth
      // code for it, minted from the still-valid IAM cookie, is exchanged like a share-link login.
      const surfaceUuid: string | null = rememberedShareLink(this.bridge.currentUser()?.email);
      if (surfaceUuid === null) {
        this.signOutWithoutTenant();
        return;
      }
      this.bridge.generateGuestAuthCode$(surfaceUuid).subscribe({
        next: (authCode: AuthCodeResponse): void => {
          void this.router.navigate([environment.iam.authProcessUrl], {
            queryParams: { ...authCode, redirect_uri: `${window.location.origin}/s/${surfaceUuid}` },
          });
        },
        error: (): void => this.signOutWithoutTenant(),
      });
      return;
    }
    // The returnUrl is deliberately NOT popped here. `/entry/tenant-select` now renders
    // `mtm-auth`'s tenant step, which exchanges the auth code itself and finishes through
    // `onAppTokenReady` -> `redirectAfterAuthentication()` — and that is what pops it.
    // Popping here left nothing to pop later: the value was carried to the picker in router
    // state and forwarded to `/entry/auth/process`, where nothing ever read it, so a
    // multi-tenant login always fell back to `getInitialRoute()` and lost the deep link.
    //
    // NoAuthGuard skips its stale-session cleanup on this route, so the SDK's persisted
    // identity and returnUrl survive the hop (and a reload of the picker).
    this.router.navigate(['/entry/tenant-select'], { state: { tenants } });
  }

  /**
   * Ends a session that has nowhere to go — no tenant and no share link to return to (e.g. a guest
   * signing in from the regular login page on a browser that never opened their link) — and points
   * them to the link they were sent. The notice never mentions signing in or out: that is internal
   * mechanics, not something a guest should be told about.
   */
  private signOutWithoutTenant(): void {
    // End the IAM session itself, not just the local one: `AuthService.logout()` only calls the IAM
    // when an app token is present, and a session landing here has none. Left alive, the IAM cookie
    // restores the session on the next page load, which lands here again and signs out again — the
    // "Signed out" notice on every visit.
    this.bridge
      .logout$()
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => {
        this.authService.logout({ message: 'no tenant and no share link' });
        void this.router.navigate(['/entry/login']).then(() => this.notifySharedLinkOnly());
      });
  }

  private async notifySharedLinkOnly(): Promise<void> {
    // This can run on the very first navigation of a page load, before the app has loaded its
    // translations — wait for them rather than show raw keys.
    if (!(this.translate.data as Record<string, string>)['login_sharedLinkOnly_title']) {
      await this.translate.use(localStorage.getItem(AUTH_LANGUAGE_STORAGE_KEY) || 'en_us');
    }
    this.notificationService.open({
      title: this.translateText('login_sharedLinkOnly_title'),
      description: this.translateText('login_sharedLinkOnly_description'),
      confirmBtn: 'OK',
    });
  }

  private translateText(key: string): string {
    return (this.translate.data as Record<string, string>)[key] ?? key;
  }

  private redirectAfterAuthentication(): void {
    // An explicit `redirect_uri` (set by `authGuestGuard` when it bounced an anonymous visitor
    // off a guest route) is the strongest signal of intent — honor it before the SDK returnUrl.
    const redirectUri: string | null = this.popRedirectUri();
    if (redirectUri && !this.isAuthEntryUrl(redirectUri)) {
      this.bridge.setReturnUrl(redirectUri);
    }

    const returnUrl = this.bridge.popReturnUrl();
    // A returnUrl that points back at the SSO callback / entry routes carries the one-time
    // auth token/code; honoring it re-enters the callback and bounces through NoAuthGuard and
    // the legacy /projects -> /workspaces redirect (the observed flicker). Ignore it
    // and compute the real landing route instead.
    if (returnUrl && !this.isAuthEntryUrl(returnUrl)) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    this.featureConfigService.getInitialRoute().subscribe((route: string[]) => this.router.navigate(route));
  }

  /**
   * Read and clear the deep link requested via `?redirect_uri=`. It is looked up on the current
   * URL first (direct hit on `/entry/login?redirect_uri=...`) and otherwise in sessionStorage,
   * where `authGuestGuard` stashed it — the query param does not survive the SSO hops.
   * Returns a same-origin path (`/foo?bar#baz`); cross-origin values are dropped so the login
   * flow can never be turned into an open redirect.
   */
  private popRedirectUri(): string | null {
    const fromUrl: string | null = new URL(window.location.href).searchParams.get('redirect_uri');
    const stored: string | null = sessionStorage.getItem(REDIRECT_URI_STORAGE_KEY);
    sessionStorage.removeItem(REDIRECT_URI_STORAGE_KEY);

    const raw: string | null = fromUrl || stored;
    if (!raw) {
      return null;
    }
    let parsed: URL;
    try {
      parsed = new URL(raw, window.location.origin);
    } catch {
      return null;
    }
    if (parsed.origin !== window.location.origin) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  /** True for the IAM callback / entry-auth URLs that must never be used as a post-login returnUrl. */
  private isAuthEntryUrl(url: string | null | undefined): boolean {
    if (!url) {
      return false;
    }
    return /\/entry\/(auth|sso|login)\b/.test(url) || /[?&](token|code)=/.test(url);
  }
}
